// Pure helpers for the vanity-subdomain add-on. The RESERVED list here MIRRORS
// the one embedded in the `admin_set_subdomain` migration — keep both in sync
// (same pattern as geo.ts ↔ _shared/geo.ts). The RPC is the real barrier; this
// is the live client-side pre-check.
export const RESERVED_SUBDOMAINS: readonly string[] = [
  "www", "app", "api", "admin", "mail", "staff", "dashboard", "login",
  "register", "auth", "book", "portfolio", "faq", "360", "tap", "v", "cdn",
  "assets", "static", "status", "help", "support", "blog", "docs",
];

const FORMAT_RE = /^[a-z0-9](?:[a-z0-9-]{1,30}[a-z0-9])$/;

export function isValidSubdomainFormat(slug: string): boolean {
  return FORMAT_RE.test(slug);
}

export function isReservedSubdomain(slug: string): boolean {
  return RESERVED_SUBDOMAINS.includes(slug.toLowerCase());
}

export function isAssignableSubdomain(slug: string): boolean {
  return isValidSubdomainFormat(slug) && !isReservedSubdomain(slug);
}

/** Vanity slug for `<slug>.andarlabs.com` / `<slug>.localhost`, else null. */
export function extractSubdomain(hostname: string): string | null {
  const host = hostname.toLowerCase().split(":")[0];
  if (/^\d+\.\d+\.\d+\.\d+$/.test(host)) return null; // IPv4
  const parts = host.split(".");
  // localhost dev: `<slug>.localhost`
  if (parts.length === 2 && parts[1] === "localhost") {
    return parts[0] === "www" || parts[0] === "app" ? null : parts[0];
  }
  // prod: `<slug>.andarlabs.com` → 3 labels
  if (parts.length !== 3) return null;
  const slug = parts[0];
  if (slug === "www" || slug === "app") return null;
  return slug;
}

/**
 * Public URL for a resource path. When the owner has a vanity `subdomain`, the
 * host becomes `<slug>.<apex>`; otherwise the app's default origin is used.
 * `baseUrl` is injectable for tests; defaults to VITE_APP_DOMAIN / origin.
 */
export function buildPublicUrl(
  subdomain: string | null | undefined,
  path: string,
  baseUrl?: string,
): string {
  const base = (
    baseUrl ??
    (import.meta.env.VITE_APP_DOMAIN as string | undefined) ??
    window.location.origin
  ).replace(/\/$/, "");
  if (!subdomain) return `${base}${path}`;
  const u = new URL(base);
  const apex = u.hostname.replace(/^www\./, "");
  return `${u.protocol}//${subdomain}.${apex}${path}`;
}
